import { Box, BoxProps, Flex, HStack, Text } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import WebLinks from '~/components/WebLinks';
import { SOCIAL_PLATFORMS } from '~/constants';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  YoutubeChannelType,
} from '~/types';
import { convertPxToRems } from '~/utils-ts';

import { Platforms } from './components/Platforms';

interface Props extends BoxProps {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  ytChannel: YoutubeChannelType;
}

export const AverageMetrics: React.FC<Props> = ({
  mediaPack = {} as DefaultMediaPackType,
  creatorBenchmarks = {} as CreatorBenchmarks,
  ytChannel,
  ...props
}) => {
  const { igFollowersCount, ttFollowersCount, ytFollowersCount } =
    creatorBenchmarks?.combinedAudience || {};
  const platformFollowers: { [key in SOCIAL_PLATFORMS]: number | undefined } = {
    [SOCIAL_PLATFORMS.instagram]: igFollowersCount,
    [SOCIAL_PLATFORMS.youtube]: ytFollowersCount,
    [SOCIAL_PLATFORMS.tiktok]: ttFollowersCount,
  };

  const hasPlatforms = !isEmpty(mediaPack.platforms);
  const hasWebLinks = !isEmpty(mediaPack.creator?.webLinks);
  const hasSeveralPlatforms = mediaPack.platforms?.length > 1;

  if (!hasPlatforms && !hasWebLinks) return null;

  return (
    <Flex
      maxWidth={convertPxToRems(396)}
      data-testid="average-metrics-section"
      flexGrow={1}
      flexDirection={{
        base: hasSeveralPlatforms ? 'column' : 'row',
        sm: 'row',
      }}
      alignItems={{ base: 'start', md: 'center' }}
      gap={{
        base: hasSeveralPlatforms ? 8 : 10,
        md: 10,
        lg: 14,
      }}
      {...props}
    >
      {hasPlatforms && (
        <Box
          display="flex"
          flexDir="column"
          height={convertPxToRems(52)}
          gap={4}
        >
          <Text
            fontSize="lg"
            fontWeight="medium"
            color="primary.500"
            lineHeight="4"
            letterSpacing="wide"
          >
            Platforms
          </Text>

          <Platforms
            platforms={mediaPack.platforms.map((platform) => ({
              name: platform as SOCIAL_PLATFORMS,
              followers: platformFollowers[platform as SOCIAL_PLATFORMS],
            }))}
            flex={{
              base: 1,
              lg: 0.8,
            }}
            height={5}
            gap={4}
            alignItems="center"
            ytChannel={ytChannel}
          />
        </Box>
      )}

      {hasWebLinks && (
        <Box
          display="flex"
          flexDir="column"
          gap={4}
          height={convertPxToRems(52)}
        >
          <Text
            fontSize="lg"
            fontWeight="medium"
            color="primary.500"
            lineHeight="4"
            letterSpacing="wide"
          >
            Links
          </Text>
          <HStack spacing={8}>
            <WebLinks
              links={mediaPack.creator?.webLinks ?? []}
              alignItems="center"
              size={4}
              gap={4}
            />
          </HStack>
        </Box>
      )}
    </Flex>
  );
};
